/**
 * @file schemas.ts
 * @brief Zod schemas for Company Admin → Employees.
 *
 * @author DES
 * @date 19-06-2026
 */

import { z } from "zod";

export const EmployeeStatusSchema = z.enum(["active", "suspended", "deleted"]);
export const EmployeeRoleSchema = z.enum(["user", "admin"]);

export const CompanyEmployeeSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    role: z.string(),
    status: EmployeeStatusSchema.or(z.string()),
    title: z.string().nullable().optional(),
    company_id: z.string().nullable().optional(),
    company_name: z.string().nullable().optional(),
    profile_image_url: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    assigned_payment_method_id: z.string().nullable().optional(),
    created_at: z.string().optional(),
    last_login_at: z.string().nullable().optional(),
    // Most recent login location (from login_events, resolved by IP). Nullable —
    // private/loopback logins resolve to no location. (08-07-2026)
    last_login_country: z.string().nullable().optional(),
    last_login_region: z.string().nullable().optional(),
    last_login_city: z.string().nullable().optional(),
    // Enterprise shared-pool seat assignment (26-07-2026).
    enterprise_seat: z.boolean().optional(),
  })
  .passthrough();

export const CompanyEmployeesResponseSchema = z.object({
  success: z.boolean().optional(),
  users: z.array(CompanyEmployeeSchema),
});

// Login-location audit row from GET /api/company/users/:id/login-history.
// All geo fields nullable (private/loopback IPs resolve to no location).
export const LoginEventSchema = z
  .object({
    id: z.string().optional(),
    ip_address: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    region: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    timezone: z.string().nullable().optional(),
    user_agent: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();

export const LoginHistoryResponseSchema = z.object({
  success: z.boolean().optional(),
  history: z.array(LoginEventSchema),
});

export type LoginEvent = z.infer<typeof LoginEventSchema>;

export const CreateEmployeeInputSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  phoneNumber: z.string().optional(),
  title: z.string().optional(),
  role: EmployeeRoleSchema.default("user"),
});

export const UpdateEmployeeInputSchema = z.object({
  first_name: z.string().min(1).optional(),
  last_name: z.string().min(1).optional(),
  // No email (B34, 26-10-2026): only the person changes it, from their own
  // Profile; a different email here is 403 EMAIL_CHANGE_SELF_ONLY.
  title: z.string().optional(),
  role: EmployeeRoleSchema.optional(),
  status: EmployeeStatusSchema.optional(),
});

export type CompanyEmployee = z.infer<typeof CompanyEmployeeSchema>;
export type CreateEmployeeInput = z.infer<typeof CreateEmployeeInputSchema>;
export type UpdateEmployeeInput = z.infer<typeof UpdateEmployeeInputSchema>;
